// Lifecycle stage metadata — labels, colors, valid transitions. Mirrors
// functions/api/_lib/validators.js SCHOOL_TRANSITIONS / EXTERNAL_TRANSITIONS.
// Kept on the frontend for immediate UI feedback (valid-drop hinting, column
// colors) — the server is still the source of truth on submit.

// Post-019 8-stage cleanup. audit_sent + proposal_sent collapsed to engaged;
// in_build/in_delivery/live/delivered collapsed to maintained; churned
// collapsed to closed_lost.
export const SCHOOL_STAGES = [
  'prospect', 'contacted', 'engaged', 'verbal_yes', 'contracted',
  'maintained', 'dormant', 'closed_lost',
];

export const EXTERNAL_STAGES = [
  'prospect', 'engaged', 'verbal_yes', 'contracted',
  'maintained', 'dormant', 'closed_lost',
];

export const SCHOOL_TRANSITIONS = {
  prospect:    ['contacted', 'engaged', 'verbal_yes', 'contracted', 'dormant', 'closed_lost'],
  contacted:   ['engaged', 'verbal_yes', 'contracted', 'dormant', 'closed_lost', 'prospect'],
  engaged:     ['verbal_yes', 'contracted', 'dormant', 'closed_lost', 'contacted'],
  verbal_yes:  ['contracted', 'closed_lost', 'engaged'],
  contracted:  ['maintained', 'closed_lost', 'verbal_yes'],
  maintained:  ['dormant', 'closed_lost'],
  dormant:     ['prospect', 'contacted'],
  closed_lost: ['contacted'],
};

export const EXTERNAL_TRANSITIONS = {
  prospect:    ['engaged', 'verbal_yes', 'contracted', 'closed_lost'],
  engaged:     ['verbal_yes', 'contracted', 'closed_lost'],
  verbal_yes:  ['contracted', 'closed_lost', 'engaged'],
  contracted:  ['maintained', 'closed_lost'],
  maintained:  ['dormant', 'closed_lost'],
  dormant:     ['contacted', 'engaged'],
  closed_lost: ['engaged'],
};

export const STAGE_REQUIRES_SUMMARY = new Set([
  'contacted', 'engaged', 'verbal_yes', 'contracted',
]);

// Active funnel columns shown on /admin/pipeline. Reduced from 6 to 4 by 019;
// Maintained added 2026-09-25 so renewed schools are visible on the board.
export const PIPELINE_COLUMNS = ['contacted', 'engaged', 'verbal_yes', 'contracted', 'maintained'];

export const PIPELINE_EXPANDED_COLUMNS = [
  'contacted', 'engaged', 'verbal_yes', 'contracted', 'maintained',
  'dormant', 'closed_lost',
];

// The board's virtual column: Engaged schools with accounts.meeting_scheduled_at
// set. Not a lifecycle stage (the stage CHECK cannot be widened), so it never
// appears in the stage lists above; Pipeline.jsx slots it after Engaged.
export const MEETING_COLUMN = 'meeting_scheduled';
export function boardColumnOf(account) {
  return account?.lifecycleStage === 'engaged' && account?.meetingScheduledAt ? MEETING_COLUMN : account?.lifecycleStage;
}

// Stages where time-in-stage is not a to-do: a school stays Maintained for
// years by design, so its cards never get the stale (amber/red) border.
export const NO_AGING_STAGES = new Set(['maintained']);

// Stage display metadata. Chip + column border use the `tone` classes.
export const STAGE_META = {
  prospect:      { label: 'Prospect',      tone: 'bg-gray-500/10 text-gray-300 border-gray-500/20' },
  dormant:       { label: 'Dormant',       tone: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
  contacted:     { label: 'Contacted',     tone: 'bg-blue-500/10 text-blue-300 border-blue-500/20' },
  engaged:       { label: 'Engaged',       tone: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20' },
  meeting_scheduled: { label: 'Meeting scheduled', tone: 'bg-violet-500/10 text-violet-300 border-violet-500/20' },
  audit_sent:    { label: 'Audit sent',    tone: 'bg-teal-500/10 text-teal-300 border-teal-500/20' },
  proposal_sent: { label: 'Proposal sent', tone: 'bg-amber-500/10 text-amber-300 border-amber-500/20' },
  verbal_yes:    { label: 'Verbal yes',    tone: 'bg-lime-500/10 text-lime-300 border-lime-500/20' },
  contracted:    { label: 'Contracted',    tone: 'bg-white/10 text-white border-white/20' },
  in_build:      { label: 'In build',      tone: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/20' },
  live:          { label: 'Live',          tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' },
  maintained:    { label: 'Maintained',    tone: 'bg-green-500/10 text-green-300 border-green-500/20' },
  closed_lost:   { label: 'Closed lost',   tone: 'bg-red-500/10 text-red-300 border-red-500/20' },
  churned:       { label: 'Churned',       tone: 'bg-orange-500/10 text-orange-300 border-orange-500/20' },
  in_delivery:   { label: 'In delivery',   tone: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/20' },
  delivered:     { label: 'Delivered',     tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' },
};

export function stageMeta(stage) {
  return STAGE_META[stage] || { label: stage || '—', tone: 'bg-gray-500/10 text-gray-400 border-gray-500/20' };
}

export function validTransitions(currentStage, type = 'school') {
  const graph = type === 'external' ? EXTERNAL_TRANSITIONS : SCHOOL_TRANSITIONS;
  return graph[currentStage] || [];
}
